import { client, withDataRequest } from 'utils/client';
import { GetJobsTable, EmployerJobTableView, JobDetails, JobResponseTypes } from 'features/jobs/types';
import { TagFilters } from 'features/programs/types';
import { DataResponse, SelectItem } from './types';
import { Tab, TableTabFilters } from 'api/types';
import { JobEmployerFormData, JobForm, JobLocations, JobOrganizationFormData, JobJobSeekerFormData, Languages } from 'features/job-form/types';
import { GetJobPostNetworksModerationTable, JobPostNetworksModerationTableResponse } from 'features/job-post-networks-moderation';
import { PostNetworkApprovalForm, GetPostNetworksModerationTabsFilters } from 'features/post-networks-moderation/types';
import { capitalize } from 'utils/string';

const mapJobData = (values: JobForm): FormData => {
  const fullAddress = (values.locationOption !== JobLocations.Remote && values.locationOption !== JobLocations.Travel && values.locationOption !== JobLocations.Overseas) ? values.fullAddress : '';
  const radius = values.locationOption === JobLocations.Multiple ? values.radius : '';
  const location = (values.fullAddress && values.locationOption !== JobLocations.Remote && values.locationOption !== JobLocations.Travel && values.locationOption !== JobLocations.Overseas) ? values.location : undefined;
  const languageProficiency = values.language !== Languages.NotRequired ? values.languageProficiency : '';
  const applyLink = values.responseType === JobResponseTypes.ApplyLink ? values.applyLink : '';
  const directResponseRecipients = values.responseType === JobResponseTypes.DirectResponse ? values.directResponseRecipients : [];

  const data = new FormData();
  data.append('title', capitalize(values.title));
  if (values.code !== undefined) data.append('code', values.code);
  data.append('description', values.description || '');
  if (values.language !== undefined) data.append('language', values.language);
  data.append('languageProficiency', languageProficiency || '');
  if (values.locationOption !== undefined) data.append('locationOption', values.locationOption);
  data.append('fullAddress', fullAddress || '');
  data.append('radius', radius || '');
  if (values.travelLocationAreas !== undefined) data.append('travelLocationAreas', values.travelLocationAreas);
  if (values.overseasLocationAreas !== undefined) data.append('overseasLocationAreas', values.overseasLocationAreas);
  if (Array.isArray(values.jobTypes)) values.jobTypes.forEach((item, index) => data.append('jobTypes[' + index + ']', item));
  if (values.payRangeMin !== undefined) data.append('payRangeMin', values.payRangeMin);
  if (values.payRangeMax !== undefined) data.append('payRangeMax', values.payRangeMax);
  if (values.payrate !== undefined) data.append('payrate', values.payrate);
  if (values.jobExpiration !== undefined) data.append('jobExpiration', values.jobExpiration);
  if (Array.isArray(values.supplementalPayOptions)) values.supplementalPayOptions.forEach((item, index) => data.append('supplementalPayOptions[' + index + ']', item));
  if (values.supplementalPayOther !== undefined) data.append('supplementalPayOther', values.supplementalPayOther);
  if (Array.isArray(values.benefits)) values.benefits.forEach((item, index) => data.append('benefits[' + index + ']', item));
  if (values.benefitsOther !== undefined) data.append('benefitsOther', values.benefitsOther);
  if (Array.isArray(values.skills)) values.skills.forEach((item, index) => data.append('skills[' + index + ']', item));
  if (Array.isArray(values.certifications)) values.certifications.forEach((item, index) => data.append('certifications[' + index + ']', item));
  if (location?.lat && location?.lng) data.append('location', JSON.stringify(location));
  if (values.niceToHaveAttributes !== undefined) data.append('niceToHaveAttributes', values.niceToHaveAttributes);
  if (values.contactName !== undefined) data.append('contactName', values.contactName);
  if (values.contactEmail !== undefined) data.append('contactEmail', values.contactEmail);
  if (values.contactPhone !== undefined) data.append('contactPhone', values.contactPhone);
  if (values.contactCalendarLink !== undefined) data.append('contactCalendarLink', values.contactCalendarLink);
  if (values.transportationToJobSite !== undefined) data.append('transportationToJobSite', values.transportationToJobSite);
  if (values.backgroundCheckGuidelines !== undefined) data.append('backgroundCheckGuidelines', values.backgroundCheckGuidelines);
  data.append('applyLink', applyLink || '');
  if (values.responseType !== undefined) data.append('responseType', values.responseType);
  if (Array.isArray(directResponseRecipients)) directResponseRecipients.forEach((item, index) => data.append('directResponseRecipients[' + index + ']', item));
  if (values.employerName !== undefined) data.append('employerName', values.employerName);
  if (values.employerLogo !== undefined) data.append('employerLogo', values.employerLogo);
  if (values.removeEmployerLogo !== undefined) data.append('removeEmployerLogo', values.removeEmployerLogo ? 'true' : 'false');
  if (values.cloneLogoFromSavedEmployerId) data.append('cloneLogoFromSavedEmployerId', values.cloneLogoFromSavedEmployerId);
  if (values.cloneLogoFromJobId) data.append('cloneLogoFromJobId', values.cloneLogoFromJobId);
  if (Array.isArray(values.programsIds)) values.programsIds.forEach((item, index) => data.append('programsIds[' + index + ']', item));
  if (Array.isArray(values.networksIds)) values.networksIds.forEach((item, index) => data.append('networksIds[' + index + ']', item));
  if (values.publishType !== undefined) data.append('publishType', values.publishType);
  if (Array.isArray(values.experienceLevels)) values.experienceLevels.forEach((item, index) => data.append('experienceLevels[' + index + ']', item));
  data.append('isPublicOnPcs', values.isPublicOnPcs ? 'true' : 'false');
  return data;
};

const jobsAPI = {
  getTable(params: GetJobsTable) {
    return client.post<EmployerJobTableView>('/jobs/get-table', withDataRequest(params));
  },
  getJobSeekerTable(params: GetJobsTable) {
    return client.post<EmployerJobTableView>('/jobs/job-seeker-get-table', withDataRequest(params));
  },
  getTabs(params: TableTabFilters) {
    return client.post<Tab[]>('/jobs/tabs', withDataRequest(params));
  },
  getJobSeekerTabs(params: TableTabFilters) {
    return client.post<Tab[]>('/jobs/job-seeker-tabs', withDataRequest(params));
  },
  validateEmployerDraft(data: JobForm) {
    return client.post<void>(`/jobs/employer-validate-draft`, withDataRequest(mapJobData(data)));
  },
  validateEmployerPublishJob(data: JobForm) {
    return client.post<void>(`/jobs/employer-validate-publish`, withDataRequest(mapJobData(data)));
  },
  validateJobSeekerDraftJob(data: JobForm) {
    return client.post<void>(`/jobs/job-seeker-validate-draft`, withDataRequest(mapJobData(data)));
  },
  validateJobSeekerPublishJob(data: JobForm) {
    return client.post<void>(`/jobs/job-seeker-validate-publish`, withDataRequest(mapJobData(data)));
  },
  addJobSeekerJob(data: JobForm, id?: string) {
    return client.post<void>(`/jobs/job-seeker-publish?jobId=${id}`, withDataRequest(mapJobData(data)));
  },
  addEmployerJob(data: JobForm, id?: string) {
    return client.post<void>(`/jobs/publish?jobId=${id}`, withDataRequest(mapJobData(data)));
  },
  saveEmployerDraft(data: JobForm, id?: string) {
    return client.post<void>(`/jobs/save-draft?jobId=${id}`, withDataRequest(mapJobData(data)));
  },
  saveJobSeekerDraft(data: JobForm, id?: string) {
    return client.post<void>(`/jobs/job-seeker-save-draft?jobId=${id}`, withDataRequest(mapJobData(data)));
  },
  organizationAdd(data: JobForm) {
    return client.post<void>(`/jobs/organization-add`, withDataRequest(mapJobData(data)));
  },
  organizationEdit(data: JobForm, id: string) {
    return client.put<void>(`/jobs/${id}/organization-edit`, withDataRequest(mapJobData(data)));
  },
  validateOrganizationPublish(data: JobForm) {
    return client.post<void>(`/jobs/organization-validate-publish`, withDataRequest(mapJobData(data)));
  },
  saveOrganizationDraft(data: JobForm, id?: string) {
    return client.post<void>(`/jobs/organization-save-draft?jobId=${id}`, withDataRequest(mapJobData(data)));
  },
  validateOrganizationDraft(data: JobForm) {
    return client.post<void>(`/jobs/organization-validate-draft`, withDataRequest(mapJobData(data)));
  },
  deleteJob(id: string) {
    return client.delete<void>(`/jobs/${id}`);
  },
  deleteJobSeekerJob(id: string) {
    return client.delete<void>(`/jobs/job-seeker/${id}`);
  },
  getSkills(params: TagFilters) {
    return client.post<DataResponse<SelectItem>>('/skills/get-select-list', withDataRequest(params));
  },
  getSpecialties(params: TagFilters) {
    return client.post<DataResponse<SelectItem>>('/specialties/get-select-list', withDataRequest(params));
  },
  getCertifications(params: TagFilters) {
    return client.post<DataResponse<SelectItem>>('/certifications/get-select-list', withDataRequest(params));
  },
  getJob(id: string) {
    return client.get<JobDetails>(`/jobs/${id}/job-details`);
  },
  getJobSeekerJob(id: string) {
    return client.get<JobDetails>(`/jobs/${id}/job-seeker-job-details`);
  },
  getEmployerFormData(id: string | undefined) {
    if (id) {
      return client.get<JobEmployerFormData>(`/jobs/${id}/employer-form-data`);
    } else {
      return client.get<JobEmployerFormData>('/jobs/employer-form-data');
    }
  },
  getJobSeekerFormData(id: string | undefined) {
    if (id) {
      return client.get<JobJobSeekerFormData>(`/jobs/${id}/job-seeker-form-data`);
    } else {
      return client.get<JobJobSeekerFormData>('/jobs/job-seeker-form-data');
    }
  },
  getOrganizationFormData(id: string | undefined) {
    if (id) {
      return client.get<JobOrganizationFormData>(`/jobs/${id}/organization-form-data`);
    } else {
      return client.get<JobOrganizationFormData>('/jobs/organization-form-data');
    }
  },
  archiveJob(id: string) {
    return client.post<void>(`/jobs/${id}/archive`);
  },
  archiveJobSeekerJob(id: string) {
    return client.post<void>(`/jobs/${id}/job-seeker-archive`);
  },
  getOrganizationTable(params: GetJobsTable) {
    return client.post<EmployerJobTableView>('/jobs/get-organization-table', withDataRequest(params));
  },
  getOrganizationTabsData(params: TableTabFilters) {
    return client.post<Tab[]>('/jobs/organization-tabs', withDataRequest(params));
  },
  organizationArchive(id: string) {
    return client.post<void>(`/jobs/${id}/organization-archive`);
  },
  increaseViewsCount(id: string) {
    return client.post<void>(`/jobs/${id}/increase-views-count`);
  },
  getJobPostNetworksModerationTabs(params: GetPostNetworksModerationTabsFilters) {
    return client.post<Tab[]>('/jobs/get-job-post-networks-moderation-tabs', withDataRequest(params));
  },
  getJobSeekerNetworksModerationTable(params: GetJobPostNetworksModerationTable) {
    return client.post<JobPostNetworksModerationTableResponse>('/jobs/get-job-post-networks-moderation-table', withDataRequest(params))
  },
  approveJobPost(jobId: string, data: PostNetworkApprovalForm) {
    return client.put<void>(`/jobs/${jobId}/job-post-networks-moderation-approve`, withDataRequest(data));
  },
  getJobPostNetworksModerationCount() {
    return client.get<number>(`/jobs/job-post-networks-moderation-count`);
  },
};

export default jobsAPI;